import axios from 'axios'
import { notifyPlanAccessNotice } from '../helpers/PlanAccessNotice'
import { clearLocalStoragePreservingConsent } from './storageConsent'
import { invalidateSharedReads } from './sharedRead'

export const localApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:4000/api/',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 180000
})

function isRaioXPublicRequest (url: string | undefined): boolean {
  return Boolean(url && /^\/?v1\/raio-x(?:\/|$)/.test(url))
}

let pendingTokenRefresh: Promise<string> | null = null

export async function refreshSessionToken (failedToken: string | null): Promise<string> {
  const currentToken = window.localStorage.getItem('sessionToken')
  if (currentToken && currentToken !== failedToken) return currentToken

  if (!pendingTokenRefresh) {
    pendingTokenRefresh = (async () => {
      const refreshToken = window.localStorage.getItem('refresh_token')
      if (!refreshToken) throw new Error('Sessão expirada')

      const baseURL = String(localApi.defaults.baseURL ?? '').replace(/\/+$/, '')
      const response = await axios.post(
        `${baseURL}/auth/refresh`,
        { refresh_token: refreshToken }
      )
      const newToken = String(response.data.session_token)
      window.localStorage.setItem('sessionToken', newToken)
      return newToken
    })().catch((error: unknown) => {
      clearLocalStoragePreservingConsent()
      const { pathname, search } = window.location
      const preserveRedirect = pathname === '/oauth/consent' || pathname === '/interno/solicitacoes'
      const redirect = preserveRedirect ? `&redirect=${encodeURIComponent(`${pathname}${search}`)}` : ''
      window.location.href = `/login?sessao_expirada=true${redirect}`
      throw error
    }).finally(() => {
      pendingTokenRefresh = null
    })
  }

  return await pendingTokenRefresh
}

localApi.interceptors.request.use((config) => {
  const isPublicAuthEndpoint = config.url?.startsWith('/auth/') && !config.url.includes('/auth/refresh')

  if (!isPublicAuthEndpoint) {
    const sessionToken = window.localStorage.getItem('sessionToken')
    if (sessionToken && config.headers) {
      config.headers.Authorization = `Bearer ${sessionToken}`
    }
  }

  return config
})

localApi.interceptors.response.use(
  (response) => {
    const { method, url } = response.config
    if (method && ['post', 'put', 'patch', 'delete'].includes(method) && url !== '/v1/home/metrics') {
      invalidateSharedReads()
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config
    if (error.response?.status === 403) notifyPlanAccessNotice(error.response.data?.detail)

    if (error.response?.status === 403 && error.response.data?.detail?.code === 'SUBSCRIPTION_REQUIRED') {
      if (window.location.pathname !== '/checkout' && window.location.pathname !== '/login') {
        window.location.assign('/checkout?motivo=renovacao')
      }
      return await Promise.reject(error)
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRaioXPublicRequest(originalRequest.url)) {
        return await Promise.reject(error)
      }

      originalRequest._retry = true
      const authorization = String(originalRequest.headers?.Authorization ?? '')
      const failedToken = authorization.startsWith('Bearer ') ? authorization.slice(7) : null
      const token = await refreshSessionToken(failedToken)
      originalRequest.headers.Authorization = `Bearer ${token}`
      return await localApi(originalRequest)
    }

    return await Promise.reject(error)
  }
)

export default localApi
